<template>
  <MtxPage>
    <template #header>
      <h1 class="tournaments__title">
        Tournaments
      </h1>

      <p>
        Crowdsource funding towards your next scientific discovery.
        <br />
        Post a tournament and rally everyone towards a problem that has always needed to be solved.
        <br />
        Work together through each iteration and track the contributions along the way.
      </p>

      <RouterLink :to="{ name: 'TournamentsCreate' }" class="btn btn--link">
        <span class="heading-6">
          <MtxSvg icon="icon-add" />
          + Create One
        </span>
      </RouterLink>
    </template>

    <div class="mb-1 text-right">
      <button
        aria-label="Filter Tournaments"
        title="Filter Tournaments"
        @click="showFilters = !showFilters"
        class="heading-6 btn--link"
      >
        <span class="mr-1">Filters</span>
        <MtxSvg icon="icon-sort" />
      </button>
    </div>

    <hr class="tournaments__divider color-blue mb-2" />

    <TournamentsList v-if="tournaments.length" :tournaments="tournaments" />

    <h2 class="heading-4 text-center mt-6" v-if="!tournaments.length && !isLoading">
      No tournaments matching your current filters
    </h2>

    <MtxLoading v-if="isLoading" />

    <div v-else-if="tournaments.length" class="text-center my-4">
      <button v-if="hasMore" :disabled="isLoading" @click="getTournaments(true)">
        load more
      </button>
      <button v-else disabled>
        no more
      </button>
    </div>

    <MtxDrawer v-model="showFilters" right bgTransition="fade" transition="fade-left">
      <h6>Sort &amp; Filter</h6>

      <form @submit.prevent>
        <MtxInput v-model="search" @input="onChange" type="search" label="Search" class="mb-3" />

        <MtxInput
          v-model="sortBy"
          :options="sortOptions"
          @change="onChange"
          type="select"
          label="Sort By"
          class="mb-3"
        />

        <div class="mb-3">
          <fieldset>
            <legend><small>Status</small></legend>
            <MtxInput
              v-model="status"
              :options="statuses"
              @change="onChange"
              type="radio"
              name="tournaments-status"
              :label="status.label"
              :disabled="isLoading"
            />
          </fieldset>
        </div>
        <div>
          <fieldset name="filter-category">
            <legend><small>Categories</small></legend>
            <MtxInput
              v-for="category in categories"
              :key="category.value"
              v-model="category.checked"
              @change="onChange"
              type="checkbox"
              :label="category.label"
              :disabled="isLoading"
            />
          </fieldset>
        </div>
        <button @click="resetFilters" type="button" class="mt-4 btn--link">
          Reset Filters
        </button>
      </form>
    </MtxDrawer>
  </MtxPage>
</template>

<script>
import debounce from "lodash.debounce"
import TournamentsList from "@/components/tournaments/TournamentsList"
import nprogress from "@/mixins/nprogress"
import API from "@/api"

export default {
  mixins: [nprogress],

  components: {
    TournamentsList
  },

  metaInfo: {
    titleTemplate: "%s | Tournaments",
    meta: [
      {
        name: "description",
        content:
          "View all Matryx tournaments. Enter tournaments for a chance to win a bounty of MTX tokens."
      }
    ]
  },

  data: () => ({
    showFilters: false,
    hasMore: false,
    tournaments: [],
    search: "",

    sortBy: "round_end",
    sortOptions: [
      {
        label: "Round Ending",
        value: "round_end"
      },
      {
        label: "Newest Created",
        value: "created_at"
      },
      {
        label: "Activity",
        value: "activity"
      },
      {
        label: "Tournament Bounty",
        value: "bounty"
      },
      {
        label: "Round Bounty",
        value: "round_bounty"
      }
    ],

    status: "open",
    statuses: [
      {
        value: "open",
        label: "Open"
      },
      {
        value: "scheduled",
        label: "Scheduled"
      },
      {
        value: "pending",
        label: "Pending"
      },
      {
        value: "review",
        label: "Under Review"
      },
      {
        value: "closed",
        label: "Closed"
      },
      {
        value: "abandoned",
        label: "Abandoned"
      }
    ],

    categories: [
      {
        value: "art",
        label: "Art",
        checked: false
      },
      {
        value: "environment",
        label: "Environment",
        checked: false
      },
      {
        value: "health",
        label: "Health",
        checked: false
      },
      {
        value: "math",
        label: "Math",
        checked: false
      },
      {
        value: "science",
        label: "Science",
        checked: false
      },
      {
        value: "technology",
        label: "Technology",
        checked: false
      }
    ]
  }),

  computed: {
    selectedCategories() {
      return this.categories.filter(item => item.checked).map(item => item.value)
    }
  },

  mounted() {
    const { search, sortBy, status, category } = this.$route.query

    if (search) {
      this.search = search
    }

    if (sortBy) {
      this.sortBy = sortBy
    }

    if (status) {
      this.status = status
    }

    if (category) {
      const categories = category.toLowerCase().split(",")
      this.categories.forEach(item => {
        item.checked = categories.includes(item.value)
      })
    }

    this.getTournaments()
  },

  methods: {
    onChange: debounce(function() {
      this.updateQueryStr()
      this.getTournaments()
    }, 750),

    updateQueryStr() {
      // Push to same route. Component won't rerender, but query will update
      this.$router.push({
        name: "Tournaments",
        query: {
          search: this.search || undefined,
          sortBy: this.sortBy || undefined,
          status: this.status || undefined,
          category: this.selectedCategories.join(",") || undefined
        }
      })
    },

    async getTournaments(append) {
      if (this.isLoading) return

      this.isLoading = true

      if (!append) {
        this.tournaments = []
      }

      const { success, data } = await API.getTournaments({
        offset: this.tournaments.length,
        search: this.search,
        sortBy: this.sortBy,
        status: this.status,
        category: this.selectedCategories
      })

      if (success) {
        this.tournaments.push(...data.tournaments)
        this.hasMore = data.tournaments.length === 12
      }

      this.isLoading = false
    },

    resetFilters() {
      this.search = ""
      this.sortBy = "round_end"
      this.status = "open"
      this.categories.forEach(item => (item.checked = false))
      this.updateQueryStr()
      this.getTournaments()
    }
  },

  watch: {
    "$route.query": {
      handler(newQuery) {
        if (Object.keys(newQuery).length === 0) {
          this.resetFilters()
        }
      }
    }
  }
}
</script>

<style lang="scss">
.tournaments {
  &__title {
    margin-bottom: 0;
  }

  &__divider {
    border: 1px solid currentColor;
  }
}
</style>
